import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { CUSTOMERENVIRONMENT } from '@app/common/app-tokens';
import { Constants } from '@app/common/constants';
import { HttpHelper } from '@app/common/http-helper';
import { ChannelTerm } from '@app/models/channel-term';
import { SimplifiedTerm } from '@app/models/simplified-term';
import { CacheService } from '@app/services/cache.service';
import { LicenseService } from '@app/services/license.service';
import { TaxonomyService } from '@app/services/taxonomy.service';
import { Environment } from '@environments/interface.environment';
import { addHours, differenceInMinutes } from 'date-fns';
import { Observable, of } from 'rxjs';
import { catchError, map, mergeMap, retry, tap } from 'rxjs/operators';
import { CultureService } from '@app/services/culture.service';
import { FeatureSettingsStoreService } from '@app/services/feature-settings-store.service';

@Injectable({
  providedIn: 'root'
})
export class ChannelService {

  constructor(
    @Inject(LOCALE_ID) private localeId: string,
    @Inject(CUSTOMERENVIRONMENT) private environment: Environment,
    private http: HttpClient,
    private cache: CacheService,
    private httpHelper: HttpHelper,
    private taxonomyService: TaxonomyService,
    private licenseService: LicenseService,
    private cultureService: CultureService,
    private featureService: FeatureSettingsStoreService) { }

  private readonly cacheKeyPrefix = 'ChannelService';
  public useCache = true;
  private channelTermsetId = this.featureService.state.channelTermsetGUID;

  getChannels(noCache?: boolean): Observable<Array<SimplifiedTerm>> {
    return this.licenseService.hasUserAny365License()
      .pipe(
        mergeMap(hasUser365License => {
          if (hasUser365License) {
            return this.getChannelsFromSharePoint(noCache);
          } else {
            return this.getChannelsFromApi();
          }
        }));
  }

  getChannelByIdAndHierarchy(termId: string): Observable<SimplifiedTerm> {
    return this.licenseService.hasUserAny365License()
      .pipe(
        mergeMap(hasUser365License => {
          if (hasUser365License) {
            return this.taxonomyService.getTermByIdWithFullHierarchy(termId);
          } else {
            return this.getChannelsFromApi()
              .pipe(
                map(c => c.findRecursive(t => t.Guid.toLowerCase() === termId.toLowerCase(), 'Terms'))
              );
          }
        }));
  }

  getChannelsFromSharePoint(noCache?: boolean, filterDeprecated = false): Observable<Array<SimplifiedTerm>> {
    const cacheKey = `${this.cacheKeyPrefix}-getChannelsFromSharePoint-${this.localeId}`;
    const channelTermsetGUID = this.channelTermsetId ? this.channelTermsetId : Constants.CHANNEL_TERMSET_GUID;

    const fetchOperation = this.taxonomyService.getTermSet(channelTermsetGUID, filterDeprecated)
      .pipe(
        map(response => {
          return response.map(term => new SimplifiedTerm(term)).sort((a, b) => a?.Name?.localeCompare(b?.Name));
        }),
        tap(data => {
          this.useCache && this.cache.set(cacheKey, data, addHours(new Date(), 1)); // expire cache within one hour
        }),
        retry(3),
        catchError(this.httpHelper.handleError({ operation: 'getChannelsFromSharePoint', result: new Array<SimplifiedTerm>(), rethrow: true, cacheKey: cacheKey })));

    if (this.useCache && !noCache) {
      const cachedData = this.cache.getCachableInfo<Array<SimplifiedTerm>>(cacheKey);
      if (cachedData) {
        if (differenceInMinutes(new Date(cachedData.AbsoluteExpirationDate), new Date) <= 30) {
          // If we have cached data and its about to expire within 30 min we check for any changes since last fetch)
          return this.taxonomyService
            .getAmountOfChangesSince(new Date(cachedData.AddedToCache))
            .pipe(
              mergeMap(amount => {
                if (amount > 0) {
                  return fetchOperation;
                } else {
                  return of(cachedData.Data)
                    .pipe(
                      tap(data => {
                        // We'll assume the cache is good and set the expire time one hour from now before we check for new changes.
                        this.cache.set(cacheKey, data, addHours(new Date(), 1)); // expire cache within one hour
                      }));
                }
              })
            );
        } else {
          return of(cachedData.Data);
        }
      }
    }
    return fetchOperation;
  }

  getChannelsFromApi(): Observable<Array<SimplifiedTerm>> {
    return this.cultureService.getLcidForCurrentLocale().pipe(
      mergeMap(lcid => {
        return this.http
          .get<Array<SimplifiedTerm>>(`${Constants.API_ENDPOINT}/channelterms/localized/${lcid}`)
          .pipe(
            map(
              results => results.map(r => new SimplifiedTerm(r)).sort((a, b) => a?.Name?.localeCompare(b?.Name)))
          );
      }),
      retry(3),
      catchError(this.httpHelper.handleError({ operation: 'getChannelsFromApi', result: null, rethrow: true })));
  }

  removeChannelFromApi(termId: string): Observable<SimplifiedTerm> {
    return this.http
      .delete<SimplifiedTerm>(`${Constants.API_ENDPOINT}/channelterms/${termId}`)
      .pipe(
        catchError(this.httpHelper.handleError({ operation: 'removeChannelFromApi', result: null, rethrow: true }))
      );
  }

  addOrUpdateChannelsInApi(terms: Array<ChannelTerm>): Observable<Array<ChannelTerm>> {
    // We need to append the siteUrl because there will be an authorization check in the backend against the workplace admin group on this site
    const httpParams = new HttpParams().append('siteUrl', encodeURI(this.environment.coreWorkplaceSiteUrl));
    return this.http.
      post<Array<ChannelTerm>>(`${Constants.API_ENDPOINT}/channelterms/`, terms, { params: httpParams })
      .pipe(
        catchError(this.httpHelper.handleError({ operation: 'addOrUpdateChannelsInApi', result: null, rethrow: true }))
      );
  }

}
